import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Toast, ToastContainer, Spinner, Table } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom'; // Added for routing
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../Styles/yellowcalfcss/CreateBill.css';
import { simplePostCall } from '../../api/ApiServices';
import ApiConfig from '../../api/ApiConfig';

export default function CreateBill() {
  const navigate = useNavigate();
  const { id } = useParams(); // If your route is /edit-bill/:id
  const location = useLocation();

  // Mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [billId, setBillId] = useState(null);

  // Form states
  const [billNumber, setBillNumber] = useState('');
  const [supplier, setSupplier] = useState('');
  const [billDate, setBillDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [items, setItems] = useState([]);

  // Current Item Input states
  const [currentItem, setCurrentItem] = useState('');
  const [currentQuantity, setCurrentQuantity] = useState('');
  const [currentUnitPrice, setCurrentUnitPrice] = useState('');

  // Validation & UI states
  const [errors, setErrors] = useState({});
  const [showItemValidation, setShowItemValidation] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);

  // Check if we are editing on mount
  useEffect(() => {
    if (id || location.state?.billData) {
      setIsEditMode(true);
      if (location.state?.billData) {
        const data = location.state.billData;
        setBillId(data.id);
        setBillNumber(data.bill_number);
        setSupplier(data.supplier_id);
        setBillDate(data.date);
        setDueDate(data.due_date);
        setItems(data.description || []);
      }
    }
  }, [id, location.state]);

  const calculateItemTotal = () => {
    return (parseFloat(currentQuantity) || 0) * (parseFloat(currentUnitPrice) || 0);
  };

  const addItem = () => {
    if (currentItem && currentQuantity && currentUnitPrice) {
      const newItem = {
        item: currentItem,
        quantity: parseFloat(currentQuantity),
        unit_price: parseFloat(currentUnitPrice), // Matching API naming
        item_total: calculateItemTotal()
      };
      setItems([...items, newItem]);
      setCurrentItem('');
      setCurrentQuantity('');
      setCurrentUnitPrice('');
      setShowItemValidation(false);
    } else {
      setShowItemValidation(true);
    }
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!billNumber.trim()) newErrors.billNumber = 'Bill Number required';
    if (!supplier) newErrors.supplier = 'Supplier required';
    if (items.length === 0) newErrors.items = 'Add at least one item';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      setShowErrorToast(true);
      return;
    }

    setIsLoading(true);
    const customer_id = localStorage.getItem("customer_id");
    const api_key = localStorage.getItem("api_key");
    const user_id = localStorage.getItem("user_id");

    const requestBody = {
      customer_id: Number(customer_id),
      api_key: api_key,
      user_id: Number(user_id),
      language: "en",
      bill_number: billNumber,
      supplier_id: Number(supplier),
      date: billDate,
      due_date: dueDate,
      description: items.map(item => ({
        item: item.item,
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
        item_total: Number(item.item_total),
        ...(isEditMode && item.id && { id: item.id }) // Include item ID if updating
      }))
    };

    // Add main ID for updates
    if (isEditMode) {
      requestBody.id = billId || Number(id);
    }

    const apiEndpoint = isEditMode ? ApiConfig.billUpdate : ApiConfig.billCreate;

    try {
      const response = await simplePostCall(apiEndpoint, JSON.stringify(requestBody));

      if (response?.result) {
        setShowSuccessToast(true);
        setTimeout(() => navigate("/accountant/bill"), 2000);
      } else {
        alert(response?.message || "Operation failed");
      }
    } catch (error) {
      console.error("API Error:", error);
      alert("Network error occurred.");
    } finally {
      setIsLoading(false);
    }
  };
// 1. Trigger the fetch when in Edit Mode
useEffect(() => {
  if (id) {
    setIsEditMode(true);
    loadBillDetails();
  }
}, [id]);

// 2. The Fetch Function
// Helper to convert DD/MM/YYYY to YYYY-MM-DD for the date input
const formatDateForInput = (dateStr) => {
  if (!dateStr || !dateStr.includes('/')) return dateStr;
  const [day, month, year] = dateStr.split('/');
  return `${year}-${month}-${day}`;
};

const loadBillDetails = async () => {
  setIsLoading(true);
  const requestBody = {
    customer_id: Number(localStorage.getItem("customer_id")),
    api_key: localStorage.getItem("api_key"),
    user_id: Number(localStorage.getItem("user_id")),
    id: Number(id), // or whatever key your API expects for the bill ID
  };

  try {
    const response = await simplePostCall(
      ApiConfig.billview, 
      JSON.stringify(requestBody)
    );

    if (response?.result && response.data?.length > 0) {
      // 1. Get Header info from the first element
      const header = response.data[0];
      
      setBillNumber(header.bill_number || "");
      setSupplier(header.bill_supplier || "");
      // Convert dates from DD/MM/YYYY to YYYY-MM-DD
      setBillDate(formatDateForInput(header.bill_date));
      setDueDate(formatDateForInput(header.bill_due_date));

      // 2. Map all rows in the array to your items list
      const mappedItems = response.data.map((row) => ({
        id: row.id, // ID for the specific item row
        item: row.bill_item_name,
        bill_item_quantity: row.bill_item_quantity,
        bill_item_price: row.bill_item_price,
        total_amount: row.total_amount
      }));
      
      setItems(mappedItems);
    }
  } catch (error) {
    console.error("Error loading bill:", error);
  } finally {
    setIsLoading(false);
  }
};
  return (
    <div className="create-wrapper">
      <Container fluid className="create-main-container">
        
        {/* TOASTS */}
        <ToastContainer position="top-end" className="p-3">
          <Toast bg="success" show={showSuccessToast} onClose={() => setShowSuccessToast(false)} delay={3000} autohide>
            <Toast.Body className="text-white">Bill {isEditMode ? 'updated' : 'created'} successfully!</Toast.Body>
          </Toast>
          <Toast bg="danger" show={showErrorToast} onClose={() => setShowErrorToast(false)} delay={3000} autohide>
            <Toast.Body className="text-white">{errors.items || "Please fill all required fields."}</Toast.Body>
          </Toast>
        </ToastContainer>

        <div className="title-section">
          <h3>{isEditMode ? 'Update Bill' : 'Create Bill'}</h3>
        </div>

        <div className="form-row-card">
          <div className="row-grid-two">
            <Form.Group>
              <Form.Label>Bill Number <span className="required-star">*</span></Form.Label>
              <Form.Control 
                type="text" 
                value={billNumber} 
                onChange={(e) => setBillNumber(e.target.value)}
                isInvalid={!!errors.billNumber}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Supplier <span className="required-star">*</span></Form.Label>
              <Form.Select 
                value={supplier} 
                onChange={(e) => setSupplier(e.target.value)}
                isInvalid={!!errors.supplier}
              >
                <option value="">Select</option>
                <option value="10">Vipin</option>
                <option value="3">AKBAR SHAN</option>
              </Form.Select>
            </Form.Group>
          </div>

          <div className="row-grid-two mt-3">
            <Form.Group>
              <Form.Label>Bill Date</Form.Label>
              <Form.Control type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Due Date</Form.Label>
              <Form.Control type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Form.Group>
          </div>

          {/* Item Input Section */}
          <div className="row-grid-four mt-4">
            <Form.Group>
              <Form.Label>Item</Form.Label>
              <Form.Select value={currentItem} onChange={(e) => setCurrentItem(e.target.value)}>
                <option value="">Select</option>
                <option value="Notebook">Notebook</option>
                <option value="Pen">Pen</option>
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Quantity</Form.Label>
              <Form.Control type="number" value={currentQuantity} onChange={(e) => setCurrentQuantity(e.target.value)} />
            </Form.Group>

            <Form.Group>
              <Form.Label>Unit Price</Form.Label>
              <Form.Control type="number" value={currentUnitPrice} onChange={(e) => setCurrentUnitPrice(e.target.value)} />
            </Form.Group>

            <Form.Group>
              <Form.Label>Total</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type="text" value={calculateItemTotal().toFixed(2)} readOnly />
                <Button variant="warning" onClick={addItem}>+</Button>
              </div>
            </Form.Group>
          </div>

          {/* Items Table Summary */}
          {items.length > 0 && (
            <Table striped bordered hover className="mt-4">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.item}</td>
                    <td>{item.bill_item_quantity}</td>
                    <td>{item.bill_item_price}</td>
                    <td>{item.total_amount}</td>
                    <td><Button variant="danger" size="sm" onClick={() => removeItem(index)}>x</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div className="submit-section mt-4">
            <Button 
              onClick={handleSubmit} 
              className="submit-btn" 
              disabled={isLoading}
            >
              {isLoading ? <Spinner size="sm" /> : (isEditMode ? 'Update Bill' : 'Create Bill')}
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}